/**
 * Office Copies wizard — InfoTrack-style title lookup + multi-document
 * order for HMLR Office Copies.
 *
 * Flow:
 *   1. Property lookup — search by address / postcode / title number.
 *      Backend hits HMLR's Search Property Information (mock today).
 *   2. Title chosen — fetch full HMLR document catalogue against that
 *      title (register, title plan, transfers, conveyances, leases,
 *      charges, restrictive covenants, easements, schedule of deeds).
 *   3. Multi-select — solicitor ticks the docs they need. Live fee
 *      total updates in the footer.
 *   4. Submit — creates one HMLR search row per selected document. Each
 *      row tracks its own status + result PDF.
 *
 * Falls back gracefully when the solicitor knows the title number
 * already and wants to skip the address lookup — they can type it
 * straight into the title field and click "Look up documents".
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { MagnifyingGlass, FileText, House, CheckCircle, Buildings, Warning } from "@phosphor-icons/react";

const DOC_TYPE_LABEL = {
  register: "Register",
  title_plan: "Title Plan",
  transfer: "Transfer",
  conveyance: "Conveyance",
  lease: "Lease",
  charge: "Charge / Mortgage",
  restrictive_covenant: "Restrictive Covenants",
  easement: "Easement",
  schedule_of_deeds: "Schedule of filed deeds",
};

function DocRow({ doc, checked, onToggle }) {
  return (
    <label
      className={`flex items-center gap-3 p-2.5 rounded-md border cursor-pointer transition ${
        checked ? "border-[#1462a0] bg-[#1462a0]/5" : "border-slate-200 hover:bg-slate-50"
      }`}
      data-testid={`doc-row-${doc.document_id}`}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onToggle}
        className="w-4 h-4"
        data-testid={`doc-check-${doc.document_id}`}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-semibold uppercase tracking-wide bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">
            {DOC_TYPE_LABEL[doc.doc_type] || doc.doc_type}
          </span>
          <span className="text-sm text-slate-800 truncate">{doc.description}</span>
        </div>
        <div className="text-[11px] text-slate-500 mt-0.5">
          Doc ID <span className="font-mono">{doc.document_id}</span>{doc.date ? ` · Dated ${doc.date}` : ""}
        </div>
      </div>
      <div className="text-sm font-semibold text-slate-700 shrink-0">£{doc.fee}</div>
    </label>
  );
}

export default function OrderOfficeCopiesDialog({ open, onOpenChange, caseId, caseData, onCreated }) {
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [titleNumber, setTitleNumber] = useState("");
  const [matchedAddress, setMatchedAddress] = useState("");
  const [docs, setDocs] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [picked, setPicked] = useState(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [bgLive, setBgLive] = useState(false);
  const [canReveal, setCanReveal] = useState(false);

  // Reset on open so each visit is a fresh wizard.
  useEffect(() => {
    if (open) {
      const seed = caseData?.property_address || caseData?.property_postcode || "";
      setQuery(seed);
      setResults([]);
      setTitleNumber(caseData?.title_number || "");
      setMatchedAddress(caseData?.property_address || "");
      setDocs([]);
      setPicked(new Set());
      if (caseData?.title_number) {
        // Pre-load docs straight away if we already know the title.
        loadDocs(caseData.title_number);
      }
      // Detect whether the live HMLR Business Gateway is connected so we
      // can drop the "Demo data only" banner once it's wired.
      API.get(`/hmlr-bg/status`)
        .then(({ data }) => setBgLive(!!(data?.configured && data?.environment === "live")))
        .catch(() => setBgLive(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handlePropertySearch = async () => {
    if (!query || query.trim().length < 2) return toast.error("Type at least 2 characters");
    setSearching(true);
    try {
      const { data } = await API.get(`/hmlr/property-search`, { params: { q: query } });
      setResults(data.results || []);
      if (!data.results?.length) toast.info("No matching titles found");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lookup failed");
    } finally {
      setSearching(false);
    }
  };

  const handlePickTitle = (r) => {
    setTitleNumber(r.title_number);
    setMatchedAddress(r.address);
    setResults([]);
    loadDocs(r.title_number);
  };

  const loadDocs = async (tn, deep = false) => {
    if (!tn) return;
    setLoadingDocs(true);
    if (!deep) { setDocs([]); setPicked(new Set()); setCanReveal(false); }
    try {
      const { data } = await API.get(`/hmlr/titles/${encodeURIComponent(tn)}/available-documents`, { params: deep ? { deep: 1 } : {} });
      setDocs(data.documents || []);
      setCanReveal(!!data.can_reveal_filed_documents);
      // Pre-tick Register + Title Plan — the two every conveyancer wants.
      const pre = new Set();
      (data.documents || []).forEach((d) => {
        if (d.kind === "oc1" || d.kind === "oc2") pre.add(d.document_id);
      });
      if (!deep) setPicked(pre);
      if (deep) toast.success(`Revealed ${(data.documents || []).length} filed document(s) from the register`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load HMLR documents");
    } finally {
      setLoadingDocs(false);
    }
  };

  const handleRevealFiled = () => loadDocs(titleNumber, true);

  const togglePick = (id) => {
    setPicked((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const totalFee = useMemo(
    () => docs.filter((d) => picked.has(d.document_id)).reduce((sum, d) => sum + (d.fee || 0), 0),
    [docs, picked],
  );

  const handleSubmit = async () => {
    if (!titleNumber) return toast.error("Pick or type a title number first");
    if (picked.size === 0) return toast.error("Select at least one document to order");
    setSubmitting(true);
    const tid = toast.loading(`Ordering ${picked.size} document${picked.size === 1 ? "" : "s"}…`);
    try {
      const ordered = [];
      for (const d of docs) {
        if (!picked.has(d.document_id)) continue;
        const { data } = await API.post(`/cases/${caseId}/searches`, {
          kind: d.kind,
          title_number: titleNumber,
          document_id: d.document_id,
          document_type: d.doc_type,
          document_description: d.description,
          filed_under: d.filed_under,
          document_type_code: d.document_type_code,
          document_date: d.date,
        });
        ordered.push(data);
      }
      ordered.forEach((o) => onCreated(o));
      toast.success(`Ordered ${ordered.length} document${ordered.length === 1 ? "" : "s"} (£${totalFee}). Click Fulfil on each row to run.`, { id: tid });
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Order failed", { id: tid });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="oc-wizard-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit, sans-serif" }}>Order Office Copies from HMLR</DialogTitle>
          <DialogDescription>
            Look up the property and pick which HMLR documents you need. Register + Title Plan are pre-selected.
          </DialogDescription>
        </DialogHeader>

        {/* Prominent DEMO DATA banner — surfaced at the top so solicitors
            don't trust the mock provider's output. Hidden automatically
            once the HMLR Business Gateway is live. */}
        {!bgLive && (
        <div
          className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 flex items-start gap-2"
          data-testid="oc-demo-banner"
        >
          <Warning weight="fill" className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
          <div className="text-xs text-amber-900">
            <strong>Demo data only.</strong> Address / title lookup is using a placeholder until the HMLR Business Gateway is connected.
            Always verify the matched address against the real title — overwrite it manually below if it&apos;s wrong, or
            {" "}
            <a
              href="https://eservices.landregistry.gov.uk/"
              target="_blank"
              rel="noreferrer"
              className="underline font-semibold"
            >
              search the HMLR Portal
            </a>
            {" "}
            for the real record.
          </div>
        </div>
        )}
        {bgLive && (
        <div
          className="rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 flex items-start gap-2"
          data-testid="oc-live-banner"
        >
          <Warning weight="fill" className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" />
          <div className="text-xs text-emerald-900">
            <strong>Live — HM Land Registry Business Gateway.</strong> Address lookups and Office Copies are ordered directly from HMLR. Each order is chargeable.
          </div>
        </div>
        )}

        <div className="space-y-4 py-1">
          {/* Step 1 — address / postcode / title lookup */}
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">
              <House weight="duotone" className="inline w-3.5 h-3.5 mr-1 text-[#1462a0]" />
              Find the title
            </Label>
            <div className="flex gap-2">
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handlePropertySearch()}
                placeholder="Address, postcode, or title number — e.g. 38 Prince of Wales Road / NR1 1LG"
                className="h-9 text-sm flex-1"
                data-testid="oc-query-input"
              />
              <Button onClick={handlePropertySearch} disabled={searching} className="bg-[#1462a0] hover:bg-[#10507f] text-white h-9 shrink-0" data-testid="oc-query-search">
                <MagnifyingGlass weight="bold" className="w-4 h-4 mr-1" />
                {searching ? "Searching…" : "Lookup"}
              </Button>
            </div>
            {results.length > 0 && (
              <div className="space-y-1 border border-slate-200 rounded-md p-2 bg-slate-50" data-testid="oc-results-list">
                {results.map((r) => (
                  <button
                    key={r.title_number}
                    onClick={() => handlePickTitle(r)}
                    className="w-full text-left px-3 py-2 rounded-md hover:bg-white border border-transparent hover:border-[#1462a0]/30 transition"
                    data-testid={`oc-result-${r.title_number}`}
                  >
                    <div className="text-sm text-slate-900">{r.address}</div>
                    <div className="text-xs text-slate-500">
                      Title <span className="font-mono text-slate-700">{r.title_number}</span> · {r.tenure}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Step 2 — title summary + manual title input + EDITABLE matched address */}
          <div className="border border-slate-200 rounded-md p-3 bg-slate-50/40 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                <Buildings weight="duotone" className="inline w-3.5 h-3.5 mr-1 text-[#1462a0]" />
                Title number
              </Label>
              {titleNumber && (
                <span className="text-[11px] text-emerald-700 inline-flex items-center gap-1">
                  <CheckCircle weight="fill" className="w-3.5 h-3.5" /> Selected
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <Input
                value={titleNumber}
                onChange={(e) => setTitleNumber(e.target.value.toUpperCase())}
                placeholder="e.g. NK123456"
                className="h-9 text-sm font-mono flex-1"
                data-testid="oc-title-input"
              />
              <Button
                onClick={() => loadDocs(titleNumber)}
                disabled={!titleNumber || loadingDocs}
                variant="outline"
                className="h-9 shrink-0"
                data-testid="oc-load-docs"
              >
                {loadingDocs ? "Loading…" : "Look up documents"}
              </Button>
            </div>
            {/*
              Editable matched-address field — the mock provider returns
              one of three Norwich fixtures regardless of the real title,
              so solicitors need to be able to overwrite it with the
              correct address. The corrected value flows through to
              SDLT5/AP1/MR01 generators as the property address.
            */}
            <div>
              <Label className="text-[11px] text-slate-500 mb-1 block">
                Matched address (editable — verify against the real title)
              </Label>
              <Input
                value={matchedAddress}
                onChange={(e) => setMatchedAddress(e.target.value)}
                placeholder="Enter the property address as it appears on the title register"
                className="h-9 text-sm"
                data-testid="oc-matched-address-input"
              />
            </div>
            {titleNumber && (
              <div className="flex flex-wrap gap-2 pt-1">
                <a
                  href={`https://eservices.landregistry.gov.uk/wps/portal/Property_Search?titleNumber=${encodeURIComponent(titleNumber)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[11px] text-[#1462a0] underline inline-flex items-center gap-1"
                  data-testid="oc-open-hmlr-portal"
                >
                  Open in HMLR Portal ↗
                </a>
                <span className="text-[11px] text-slate-400">·</span>
                <span className="text-[11px] text-slate-500">
                  Until Business Gateway is connected, verify the title against the real HMLR record before ordering.
                </span>
              </div>
            )}
          </div>

          {/* Step 3 — pick documents */}
          {docs.length > 0 && (
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                <FileText weight="duotone" className="inline w-3.5 h-3.5 mr-1 text-[#1462a0]" />
                Available HMLR documents ({docs.length})
              </Label>
              <div className="space-y-1.5" data-testid="oc-docs-list">
                {docs.map((d) => (
                  <DocRow
                    key={d.document_id}
                    doc={d}
                    checked={picked.has(d.document_id)}
                    onToggle={() => togglePick(d.document_id)}
                  />
                ))}
              </div>
              {canReveal && (
                <button
                  type="button"
                  onClick={handleRevealFiled}
                  disabled={loadingDocs}
                  data-testid="oc-reveal-filed"
                  className="text-xs font-semibold text-[#1462a0] hover:text-[#10507f] underline disabled:opacity-50"
                >
                  {loadingDocs ? "Reading register…" : "Reveal filed documents (lease, transfers, charges…) — reads the register"}
                </button>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="border-t border-slate-100 pt-3 mt-2">
          <div className="flex-1 text-sm text-slate-600 self-center" data-testid="oc-total-fee">
            {picked.size > 0 && (
              <>
                <strong className="text-slate-900">{picked.size} document{picked.size === 1 ? "" : "s"}</strong>
                {" · "}
                Total fee <strong className="text-slate-900">£{totalFee}</strong>
              </>
            )}
          </div>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting} data-testid="oc-cancel">Cancel</Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || picked.size === 0 || !titleNumber}
            className="bg-[#1462a0] hover:bg-[#10507f] text-white"
            data-testid="oc-confirm"
          >
            {submitting ? "Ordering…" : `Order ${picked.size || ""} document${picked.size === 1 ? "" : "s"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
